import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Product } from 'src/graphql/types';

const makePrefixCondition = (prefix: string) => (products: Product[]) => {
  return products.some(product => product.code.startsWith(prefix));
};

type Props = {
  products: Product[];
};

const TermAndConditions: React.FC<Props> = ({ products }) => {
  const intl = useIntl();

  const linksEl = useMemo(() => {
    const LINK_CONFIGS = [
      {
        label: <FormattedMessage id="term-and-conditions.vhis.standard" />,
        url: intl.formatMessage({ id: 'link.vhis.standard' }),
        condition: makePrefixCondition('vhis-standard'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.vhis.flexi-regular" />,
        url: intl.formatMessage({ id: 'link.vhis.flexi-regular' }),
        condition: makePrefixCondition('vhis-flexi-regular'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.vhis.flexi-plus" />,
        url: intl.formatMessage({ id: 'link.vhis.flexi-plus' }),
        condition: makePrefixCondition('vhis-flexi-plus'),
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.vhis.flexi-premium-semi-private" />
        ),
        url: intl.formatMessage({ id: 'link.vhis.flexi-premium-semi-private' }),
        condition: makePrefixCondition('vhis-flexi-premium-semi-private'),
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.vhis.flexi-premium-private" />
        ),
        url: intl.formatMessage({ id: 'link.vhis.flexi-premium-private' }),
        condition: makePrefixCondition('vhis-flexi-premium-private'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.medical.cancer-fighter-100" />,
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-100' }),
        condition: makePrefixCondition('cancer-fighter-100'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.medical.cancer-fighter-200" />,
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-200' }),
        condition: makePrefixCondition('cancer-fighter-200'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.medical.cancer-fighter-300" />,
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-300' }),
        condition: makePrefixCondition('cancer-fighter-300'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.accidental-medical.touch-wood-protector" />,
        url: intl.formatMessage({ id: 'link.accidental-medical.touch-wood-protector' }),
        condition: makePrefixCondition('touch-wood-protector'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.life.term-life" />,
        url: intl.formatMessage({ id: 'link.life.term-life' }),
        condition: makePrefixCondition('term-life'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.critical-illness.term-ci" />,
        url: intl.formatMessage({ id: 'link.critical-illness.term-ci' }),
        condition: makePrefixCondition('term-ci'),
      },
      {
        label: <FormattedMessage id="term-and-conditions.critical-illness.term-ci-plus" />,
        url: intl.formatMessage({ id: 'link.critical-illness.term-ci-plus' }),
        condition: makePrefixCondition('term-ci-plus'),
      },
    ];

    const configs = LINK_CONFIGS.filter(config => config.condition(products));

    if (configs.length > 0) {
      const formattedMessageId = configs.length === 1 ? "term-and-conditions.3" : "term-and-conditions.2"
      return (
        <>
          <FormattedMessage
            id={formattedMessageId}
            values={{
              links: () =>
                configs.map((config, ix) => {
                  return (
                    <React.Fragment key={ix}>
                      <Link
                        href={config.url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {config.label}
                      </Link>
                      {ix !== configs.length - 1 && <span> / </span>}
                    </React.Fragment>
                  );
                }),
            }}
          />
        </>
      );
    } else {
      return null;
    }
  }, [intl, products]);

  return (
    <Typography variant="caption">
      <FormattedMessage
        id="term-and-conditions.1"
        values={{
          // eslint-disable-next-line react/display-name
          a: msg => (
            <Link
              href={intl.formatMessage({ id: 'link.ia.levy' })}
              target="_blank"
              rel="noreferrer noopener"
            >
              {msg}
            </Link>
          ),
        }}
      />
      {linksEl}
    </Typography>
  );
};

export default TermAndConditions;
